import type { Chamber, OfficeLevel } from '../../types/lobbyDay'

/** The `<select>` value meaning "no chamber filter". */
export const CHAMBER_FILTER_ANY = ''

const LEVEL_PREFIX = 'level:'

/** The single source of truth for the state/federal split. A `Chamber` added later that is
 *  missing from this map is a **tsc error** at this literal — the client counterpart of the
 *  backend's `OfficeLevelsTests.Every_Chamber_Belongs_To_Exactly_One_Level` totality proof. */
export const CHAMBER_LEVELS: Record<Chamber, OfficeLevel> = {
  StateHouse: 'State',
  StateSenate: 'State',
  USHouse: 'Federal',
  USSenate: 'Federal',
}

export const CHAMBER_LABELS: Record<Chamber, string> = {
  StateSenate: 'State Senate',
  StateHouse: 'State House',
  USHouse: 'US House',
  USSenate: 'US Senate',
}

const isChamber = (v: string): v is Chamber => Object.prototype.hasOwnProperty.call(CHAMBER_LABELS, v)

/** Encode an `OfficeLevel` into the picker value `chamberFilterParams` decodes back into
 *  `{ level }`. The inverse of that function — kept here, next to `LEVEL_PREFIX`, so a caller
 *  never has to spell `'level:State'`/`'level:Federal'` as a literal. */
export function levelFilterValue(level: OfficeLevel): string {
  return `${LEVEL_PREFIX}${level}`
}

/** Decode a picker value into the query params the legislator API understands.
 *  An unrecognised value degrades to "no filter" rather than a 400 — the picker is UX,
 *  and a stale bookmarked value should show everything, not fail. */
export function chamberFilterParams(value: string): { chamber?: Chamber; level?: OfficeLevel } {
  if (value === `${LEVEL_PREFIX}State`) return { level: 'State' }
  if (value === `${LEVEL_PREFIX}Federal`) return { level: 'Federal' }
  if (isChamber(value)) return { chamber: value }
  return {}
}

/** In-memory counterpart for surfaces that already hold every row (Outreach). */
export function matchesChamberFilter(chamber: Chamber, value: string): boolean {
  const { chamber: c, level } = chamberFilterParams(value)
  if (c) return chamber === c
  if (level) return CHAMBER_LEVELS[chamber] === level
  return true
}
